import { CoordinatesDto, LocationDto } from '../../../../shared/models/common';

export enum VisitCycleStatus {
  Draft = 'Draft',
  Active = 'Active',
  Paused = 'Paused',
  Completed = 'Completed',
  Cancelled = 'Cancelled'
}

export enum VisitCycleType {
  Continuous = 'Continuous',
  OneTime = 'OneTime'
}

export enum VisitCycleExecutionStatus {
  Planned = 'Planned',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Cancelled = 'Cancelled'
}

export interface VisitCycleExecutionVisitPlanDto {
  visitPlanId: number;
  visitPlanName: string;
}

export interface VisitCycleExecutionOccupiedDayDto {
  /** ISO YYYY-MM-DD. */
  date: string;
  dayNumber: number;
}

export interface VisitCycleExecutionSummaryDto {
  id: number;
  executionNumber: number;
  status: VisitCycleExecutionStatus;
  plannedStartDate: string;
  plannedEndDate: string;
  consultantId: number;
  consultantName: string;
  /**
   * VisitPlans desta execução (id + name) em ordem de DayNumber. A execução pode ser
   * um subconjunto dos VisitPlans atuais do template (snapshot da materialização, exceto
   * edições feitas no escopo da execução) E também pode incluir VPs que já foram removidos
   * do template — esses dias são preservados via NULL-FK quando a edição do template os remove.
   * O painel usa o name de cada entrada para renderizar corretamente a UI de "roteiros
   * selecionados", mesmo para ids que não estão mais em cycle.visitPlans.
   */
  visitPlans: VisitCycleExecutionVisitPlanDto[];
  /**
   * Datas atualmente ocupadas por dias non-Skipped desta execução. O calendário do Step 2
   * pinta essas datas como indisponíveis quando o admin está editando outra execução,
   * espelhando o guard cross-exec do CalendarChangesApplier (o backend rejeita a atribuição
   * com 400 "já está atribuída na execução #N"). Trazer isso à tona de antemão evita o
   * round-trip click→400.
   */
  occupiedDays: VisitCycleExecutionOccupiedDayDto[];
}

export type EditingScope = 'template' | { executionId: number };

// Batch apply of calendar changes scoped to an execution.
// plannedDate=null unassigns the day (PlannedDate nullable in execution).
export interface DayAssignmentChangeDto {
  executionDayId: number;
  plannedDate: string | null; // YYYY-MM-DD or null (unassign)
}

export interface TruncatedExecutionDto {
  executionId: number;
  executionNumber: number;
}

export interface ShiftedExecutionDto {
  executionId: number;
  executionNumber: number;
  newPlannedStartDate: string;
  newPlannedEndDate: string;
}

export interface CalendarChangesPreviewDto {
  wouldTruncateExecutions: TruncatedExecutionDto[];
  wouldShiftExecutions: ShiftedExecutionDto[];
}

export interface CalendarChangesResultDto {
  execution: VisitCycleExecutionSummaryDto;
  truncatedExecutions: TruncatedExecutionDto[];
  shiftedExecutions: ShiftedExecutionDto[];
}

export interface ScopeDropdownItem {
  label: string;
  value: EditingScope;
  dateRange?: string;
}

export enum RepeatIntervalUnit {
  Days = 'Days',
  Weeks = 'Weeks',
  Months = 'Months'
}

// ==================== Card Listing Types ====================

export enum ConsultantActivityTag {
  None = 'None',
  OnRoute = 'OnRoute',
  Paused = 'Paused',
  InternalActivity = 'InternalActivity',
  TimeBank = 'TimeBank',
  Workshop = 'Workshop',
  Visiting = 'Visiting',
  Offline = 'Offline'
}

export interface ConsultantInfo {
  id: number;
  name: string;
  avatarUrl: string | null;
}

export enum CycleCardBannerKind {
  CurrentRoute = 'CurrentRoute',
  LastExecutedPaused = 'LastExecutedPaused'
}

export interface CycleCardBanner {
  kind: CycleCardBannerKind;
  dayId: number;
  dayTitle: string;
  dayNumber: string;
  dayType: VisitCycleDayType;
  executedDate: string | null;
}

export interface Progress {
  completedDays: number;
  totalDays: number;
  percentComplete: number;
}

export interface VisitCycleCard {
  id: number;
  name: string;
  status: VisitCycleStatus;
  statusLabel: string;
  activityTag: ConsultantActivityTag;
  activityTagLabel: string;
  consultant: ConsultantInfo;
  periodLabel: string;
  routeDays: number;
  internalDays: number;
  totalStores: number;
  totalKm: number;
  banner: CycleCardBanner | null;
  progress: Progress;
  createdAt: string;
}

export interface VisitCycleCardsResult {
  items: VisitCycleCard[];
  totalCount: number;
  filteredCount: number;
  page: number;
  pageSize: number;
}

export interface VisitCycleCardFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  visitPlanId?: number;
  consultantId?: number;
  status?: VisitCycleStatus;
  type?: VisitCycleType;
  periodStart?: string;
  periodEnd?: string;
  activityTag?: ConsultantActivityTag;
}

export enum VisitCycleStep {
  BasicConfiguration = 1,
  CalendarAssignment = 2
}

export enum VisitCycleDayType {
  Route = 'Route',
  Displacement = 'Displacement',
  InternalWork = 'InternalWork',
  TimeBank = 'TimeBank',
  Workshop = 'Workshop',
  DependentRoute = 'DependentRoute'
}

export enum ActivityType {
  TimeBank = 'TimeBank',
  InternalWork = 'InternalWork',
  Displacement = 'Displacement',
  Workshop = 'Workshop'
}

export enum ActivityRepetitionType {
  Always = 'Always',
  EveryN = 'EveryN',
  Never = 'Never'
}

export enum RecurrenceFrequency {
  Daily = 'Daily',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
  Yearly = 'Yearly'
}

export interface VisitCycleVisitPlanDto {
  id: number;
  visitPlanId: number;
  visitPlanName: string;
  order: number;
  isCompleted: boolean;
  completedAt: string | null;
  totalDays: number;
}

export interface VisitCycleDetailsDto {
  id: number;
  name: string;
  visitPlans: VisitCycleVisitPlanDto[];
  visitPlanNames: string[];
  consultantId: number;
  consultantName: string;
  status: VisitCycleStatus;
  type: VisitCycleType;
  lastStep: VisitCycleStep;
  startDate: string;
  endDate?: string | null;
  avoidWeekends: boolean;
  considerHolidays: boolean;
  repeatIntervalValue?: number | null;
  repeatIntervalUnit?: RepeatIntervalUnit | null;
  recurrence?: RecurrenceDto | null;
  days: VisitCycleDayDto[];
  totalDays: number;
  assignedDays: number;
  createdAt: string;
  activatedAt?: string | null;
  completedAt?: string | null;
  editableExecutions?: VisitCycleExecutionSummaryDto[] | null;
  etag?: string | null;
  // Data mínima permitida pra atribuição no template (ISO YYYY-MM-DD).
  // Draft → cycle.StartDate; Active/Paused → última exec não-cancelada PlannedEndDate+1.
  // Usada pelo calendário pra bloquear UI antes do backend rejeitar.
  templateMinAssignableDate?: string | null;
}

export interface VisitCycleListItemDto {
  id: number;
  name: string;
  visitPlanNames: string[];
  consultantName: string;
  status: VisitCycleStatus;
  type: VisitCycleType;
  startDate: string;
  endDate?: string | null;
  totalDays: number;
  assignedDays: number;
  createdAt: string;
}

export enum VisitCycleExecutionDayStatus {
  Pending = 'Pending',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Skipped = 'Skipped'
}

export interface VisitCycleDayDto {
  id: number;
  dayNumber: number;
  type: VisitCycleDayType;
  assignedDate?: string | null;
  title: string;
  description?: string;
  cities: string[];
  badges: string[];
  storeCount: number;
  isIntermunicipal: boolean;
  returnType?: string;
  isAssigned: boolean;
  parentDayId?: number;
  hasPreWorkshopSnapshot: boolean;
  routeSummary?: VisitCycleRouteSummaryDto;
  activity?: ActivityDto;
  executionStatus?: VisitCycleExecutionDayStatus | null;
  visitPlanId?: number | null;
  visitPlanName?: string | null;
  sourceDayNumber?: number | null;
}

export interface ActivityDto {
  id: number;
  activityType: ActivityType;
  activityTypeLabel: string;
  repetitionType: ActivityRepetitionType;
  repetitionTypeLabel: string;
  repetitionInterval?: number;
  origin?: LocationDto;
  destination?: LocationDto;
  startTime?: string;
  endTime?: string;
  workshopTitle?: string;
  base?: LocationDto;
  workshop?: LocationDto;
  activeProgressId?: number;
  lastCheckInAt?: string;
  lastCheckOutAt?: string;
  hasActiveCheckIn: boolean;
  completedCheckouts: number;
}

export interface VisitCycleRouteSummaryDto {
  id: number;
  storeCount: number;
  drivingTime: string;
  storeTime: string;
  totalDistance: string;
  requiresOvernight: boolean;
}

export interface VisitCycleRouteDto {
  requiresExecutionNormalization?: boolean;
  executionEditVersion?: string;
  id: number;
  visitCycleDayId: number;
  start: CoordinatesDto;
  end: CoordinatesDto;
  totalDistanceKm: number;
  drivingTimeSeconds: number;
  visitTimeMinutes: number;
  breakTimeMinutes: number;
  requiresOvernight: boolean;
  isOverCapacity: boolean;
  polyline?: string;
  summary: VisitCycleRouteExecutionSummaryDto;
  stops: VisitCycleRouteStopDto[];
  dayProgress?: DayProgressSummaryDto;
  config?: VisitCycleRouteConfigDto;
  etag?: string;
}

export interface VisitCycleRouteExecutionSummaryDto {
  totalStores: number;
  visitedStores: number;
  nextStopId?: number;
  activeStopId?: number;
}

export interface DayProgressSummaryDto {
  isStarted: boolean;
  startedAt?: string;
  isCompleted: boolean;
  activeProgressId?: number;
}

export interface VisitCycleRouteConfigDto {
  startTime: string;
  endTime: string;
  lunchWindowStart: string;
  lunchWindowEnd: string;
  lunchDurationMinutes: number;
  visitDurationMinutes: number;
  allowOvernight: boolean;
  baseLocation: LocationDto;
}

export interface VisitCycleRouteStopDto {
  id: number;
  type: string;
  order: number;
  time: string;
  endTime?: string;
  title: string;
  location: CoordinatesDto;
  duration?: string;
  store?: VisitCycleRouteStoreDto;
  progress?: StopProgressSummaryDto;
  travelFromPrevious?: VisitCycleRouteStopTravelDto;
  isLocked?: boolean;
}

export interface VisitCycleRouteStoreDto {
  storeId?: number;
  clientId?: number;
  googlePlaceId?: string;
  name?: string;
  address?: string;
  phoneNumber?: string;
  city?: string;
}

export interface VisitCycleRouteStopTravelDto {
  distanceKm: number;
  durationSeconds: number;
  encodedPolyline?: string;
}

export interface StopProgressSummaryDto {
  badge?: BadgeDto;
  activeProgressId?: number;
  lastCheckInAt?: string;
  lastCheckOutAt?: string;
  hasActiveCheckIn: boolean;
  completedCheckouts: number;
}

export interface BadgeDto {
  text: string;
  severity: string;
}

export interface RecurrenceDto {
  frequency: RecurrenceFrequency;
  interval: number;
  weekDays?: number[];
  monthDay?: number;
  count?: number;
  endDate?: string;
}

export interface VisitCycleFilterOptionsDto {
  statuses: { value: VisitCycleStatus; label: string }[];
  types: { value: VisitCycleType; label: string }[];
  consultants: { id: number; name: string }[];
  visitPlans: { id: number; name: string }[];
}

export interface VisitCycleCalendarDto {
  year: number;
  month: number;
  days: CalendarDayDto[];
  holidays: HolidayDto[];
  routeDiscontinuityCount?: number;
  holidayConflictBaseCount?: number;
  holidayConflictDestinationCount?: number;
}

export enum HolidayImpact {
  None = 'None',
  NationalSkip = 'NationalSkip',
  BaseSkip = 'BaseSkip',
  DestinationOperational = 'DestinationOperational',
}

export enum RouteContinuity {
  Continuous = 'Continuous',
  Discontinuous = 'Discontinuous',
}

export interface CalendarDayDto {
  date: string;
  isWeekend: boolean;
  isHoliday: boolean;
  holidayName?: string;
  assignedDay?: VisitCycleDayDto;
  isProjected?: boolean;
  holidayImpact?: HolidayImpact | null;
  routeContinuity?: RouteContinuity | null;
  // 1 entry por Holiday distinto no dia, com cidade/UF resolvidos no backend pra UI
  // compor tooltip rico. Opcional: backend antigo ou dias sem feriado retornam null.
  holidayDetails?: HolidayDetailDto[] | null;
}

export interface HolidayDto {
  id: number;
  name: string;
  date: string;
  type: string;
}

export interface HolidayDetailDto {
  name: string;
  type: string;          // 'Nacional' | 'Estadual' | 'Municipal'
  state?: string | null; // UF para Estadual/Municipal; null para Nacional
  cityName?: string | null; // Cidade resolvida do IbgeCode; null para Nacional/Estadual ou IBGE sem mapping
}


export interface RecurrenceRequest {
  frequency: RecurrenceFrequency;
  interval?: number;
  weekDays?: number[];
  monthDay?: number;
  count?: number;
  endDate?: string;
}

export interface CreateVisitCycleRequest {
  visitPlanIds: number[];
  consultantId: number;
  type: VisitCycleType;
  startDate: string;
  endDate?: string | null;
  avoidWeekends?: boolean;
  considerHolidays?: boolean;
  repeatIntervalValue?: number | null;
  repeatIntervalUnit?: RepeatIntervalUnit | null;
  recurrence?: RecurrenceRequest;
}

export interface UpdateVisitCycleRequest {
  visitPlanIds?: number[];
  consultantId?: number;
  type?: VisitCycleType;
  startDate?: string;
  endDate?: string | null;
  avoidWeekends?: boolean;
  considerHolidays?: boolean;
  repeatIntervalValue?: number | null;
  repeatIntervalUnit?: RepeatIntervalUnit | null;
  recurrence?: RecurrenceRequest;
  clearRecurrence?: boolean;
}

export interface AddDayRequest {
  // Âncora da inserção pela identidade do dia (Id), não pela posição. O DayNumber é ordinal
  // volátil (renumera no flush de mudanças pendentes) — mandar posição inseria no lugar errado.
  // null = inserir no início.
  afterDayId: number | null;
  type: VisitCycleDayType;
  title: string;
  description?: string;
  activity?: ActivityRequest;
}

export interface ActivityRequest {
  activityType: ActivityType;
  repetitionType: ActivityRepetitionType;
  repetitionInterval?: number;
  origin?: LocationDto;
  destination?: LocationDto;
  startTime?: string;
  endTime?: string;
  workshopTitle?: string;
  base?: LocationDto;
  workshop?: LocationDto;
}

export interface AddRouteStopRequest {
  googlePlaceId: string;
  storeName: string;
  storeAddress: string;
  cityName?: string;
  latitude: number;
  longitude: number;
  phoneNumber?: string;
  insertAtOrder?: number;
}

export interface UpdateRouteStopRequest {
  storeName?: string;
  storeAddress?: string;
  phoneNumber?: string;
  serviceDurationMinutes?: number;
}

export interface ReorderRouteStopsRequest {
  stopOrders: { stopId: number; newOrder: number }[];
}

export class VisitCycleFilters {
  page: number = 1;
  pageSize: number = 10;
  searchTerm?: string;
  consultantId?: number;
  visitPlanId?: number;
  status?: VisitCycleStatus;
  type?: VisitCycleType;
}

export type CicloType = 'Continuo' | 'Pontual';

export interface CicloStepInfo {
  step: CicloStep;
  number: number;
  title: string;
  description: string;
  route: string;
}

export type CicloStep =
  | 'ConfigurarCiclo'
  | 'AjustarCalendario';

export const CICLO_STEPS: CicloStepInfo[] = [
  {
    step: 'ConfigurarCiclo',
    number: 1,
    title: 'Configurar Ciclos',
    description: 'Base, datas e repetições',
    route: '',
  },
  {
    step: 'AjustarCalendario',
    number: 2,
    title: 'Ajustar Calendário e Dias',
    description: 'Atribua as rotas em dias do calendário',
    route: 'ajustar-calendario',
  },
];

export function getCicloStepNumber(step: CicloStep): number {
  return CICLO_STEPS.find((s) => s.step === step)?.number ?? 1;
}

export function getCicloStepFromNumber(number: number): CicloStep | undefined {
  return CICLO_STEPS.find((s) => s.number === number)?.step;
}

export function getCicloStepRoute(stepNumber: number): string | undefined {
  return CICLO_STEPS.find((s) => s.number === stepNumber)?.route;
}

export interface RoteiroTemplate {
  id: number;
  name: string;
  baseCityName: string;
  routeCount: number;
  storeCount: number;
}

export interface ConsultorRef {
  id: number;
  nome: string;
  email?: string;
}

export function getStatusLabel(status: VisitCycleStatus): string {
  switch (status) {
    case VisitCycleStatus.Draft: return 'Rascunho';
    case VisitCycleStatus.Active: return 'Ativo';
    case VisitCycleStatus.Paused: return 'Pausado';
    case VisitCycleStatus.Completed: return 'Concluído';
    case VisitCycleStatus.Cancelled: return 'Cancelado';
    default: return 'Desconhecido';
  }
}

export function getStatusVariant(status: VisitCycleStatus): 'gray' | 'green' | 'yellow' | 'blue' | 'red' {
  switch (status) {
    case VisitCycleStatus.Draft: return 'gray';
    case VisitCycleStatus.Active: return 'green';
    case VisitCycleStatus.Paused: return 'yellow';
    case VisitCycleStatus.Completed: return 'blue';
    case VisitCycleStatus.Cancelled: return 'red';
    default: return 'gray';
  }
}

export function getTypeLabel(type: VisitCycleType): string {
  switch (type) {
    case VisitCycleType.Continuous: return 'Contínuo';
    case VisitCycleType.OneTime: return 'Pontual';
    default: return 'Desconhecido';
  }
}

export function getDayTypeLabel(type: VisitCycleDayType): string {
  switch (type) {
    case VisitCycleDayType.Route: return 'Rota';
    case VisitCycleDayType.Displacement: return 'Deslocamento';
    case VisitCycleDayType.InternalWork: return 'Trabalho Interno';
    case VisitCycleDayType.TimeBank: return 'Banco de Horas';
    case VisitCycleDayType.Workshop: return 'Workshop';
    case VisitCycleDayType.DependentRoute: return 'Rota Vinculada';
    default: return 'Desconhecido';
  }
}

export function getDayTypeIcon(type: VisitCycleDayType): string {
  switch (type) {
    case VisitCycleDayType.Route: return 'route';
    case VisitCycleDayType.Displacement: return 'directions_car';
    case VisitCycleDayType.InternalWork: return 'work';
    case VisitCycleDayType.TimeBank: return 'schedule';
    case VisitCycleDayType.Workshop: return 'school';
    case VisitCycleDayType.DependentRoute: return 'link';
    default: return 'help';
  }
}

export function getActivityTypeLabel(type: ActivityType): string {
  switch (type) {
    case ActivityType.TimeBank: return 'Banco de Horas';
    case ActivityType.InternalWork: return 'Trabalho Interno';
    case ActivityType.Displacement: return 'Deslocamento';
    case ActivityType.Workshop: return 'Workshop';
    default: return 'Desconhecido';
  }
}

export function getActivityRepetitionLabel(type: ActivityRepetitionType, interval?: number): string {
  switch (type) {
    case ActivityRepetitionType.Always: return 'Repetir em todas as execuções';
    case ActivityRepetitionType.EveryN: return `Repetir a cada ${interval || 'N'} execuções`;
    case ActivityRepetitionType.Never: return 'Nunca repetir';
    default: return 'Desconhecido';
  }
}

export function activityTypeToDayType(activityType: ActivityType): VisitCycleDayType {
  switch (activityType) {
    case ActivityType.TimeBank: return VisitCycleDayType.TimeBank;
    case ActivityType.InternalWork: return VisitCycleDayType.InternalWork;
    case ActivityType.Displacement: return VisitCycleDayType.Displacement;
    case ActivityType.Workshop: return VisitCycleDayType.Workshop;
  }
}

export function getActivityTagLabel(tag: ConsultantActivityTag): string {
  switch (tag) {
    case ConsultantActivityTag.None: return '';
    case ConsultantActivityTag.OnRoute: return 'Agora em Rota';
    case ConsultantActivityTag.Paused: return 'Pausado';
    case ConsultantActivityTag.InternalActivity: return 'Em Atividade Interna';
    case ConsultantActivityTag.TimeBank: return 'Banco de Horas';
    case ConsultantActivityTag.Workshop: return 'Em Workshop';
    case ConsultantActivityTag.Visiting: return 'Em Visita';
    case ConsultantActivityTag.Offline: return 'Offline';
    default: return '';
  }
}

export function getActivityTagVariant(tag: ConsultantActivityTag): 'green' | 'yellow' | 'blue' | 'purple' | 'gray' | 'red' {
  switch (tag) {
    case ConsultantActivityTag.OnRoute: return 'green';
    case ConsultantActivityTag.Paused: return 'yellow';
    case ConsultantActivityTag.InternalActivity: return 'blue';
    case ConsultantActivityTag.TimeBank: return 'purple';
    case ConsultantActivityTag.Workshop: return 'blue';
    case ConsultantActivityTag.Visiting: return 'green';
    case ConsultantActivityTag.Offline: return 'gray';
    default: return 'gray';
  }
}
